import { useMemo, useState } from "react";
import { fmtMoney, fmtPct } from "../parsers/xmlParser";
import { useSnapshotStore } from "../hooks/useSnapshotStore";
import { useReportSendLog } from "../hooks/useReportSendLog";
import { useLogoStore } from "../hooks/useLogoStore";
import { useSettingsStore } from "../hooks/useSettingsStore";
import { applyClientOverrides } from "../utils/clientOverrides";
import { openReport } from "../utils/pdfEngine";
import { supabase } from "../lib/supabase";
import { processSnapshotFunds, computeSnapshotTotals, buildSnapshotReportHtml } from "../utils/buildSnapshotReport";

function Modal({ title, icon, color, onClose, children, headerExtra, inline }) {
  return (
    <div style={{
      position: inline ? "absolute" : "fixed", inset: 0,
      background: inline ? "rgba(30, 41, 59, 0.4)" : "rgba(0,0,0,0.5)",
      backdropFilter: inline ? "blur(2px)" : "none",
      zIndex: inline ? 100 : 1000, display: "flex", alignItems: "flex-start", justifyContent: "center",
      padding: inline ? "12px" : "24px 16px", overflowY: "auto",
    }} onClick={onClose}>
      <div style={{
        background: "#f8fafc", borderRadius: 16, width: "100%", maxWidth: inline ? "100%" : 1200,
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -6px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
        marginTop: inline ? "10px" : "0",
        marginBottom: inline ? "20px" : "0",
      }} onClick={e => e.stopPropagation()}>
        <div style={{
          background: `linear-gradient(135deg, ${color}, ${color}dd)`,
          padding: "16px 24px", color: "white",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{icon} {title}</div>
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>ניהול תמונת לקוח והמלצות יועץ</div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
            {headerExtra}
            <button onClick={onClose} style={{
              background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.4)",
              color: "white", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontSize: 14,
            }}>✕</button>
          </div>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

function buildComposeUrl(provider, to, subject, body) {
  const enc = encodeURIComponent;
  if (provider === "gmail")
    return `https://mail.google.com/mail/?view=cm&to=${enc(to)}&su=${enc(subject)}&body=${enc(body)}`;
  if (provider === "outlook")
    return `https://outlook.live.com/mail/0/deeplink/compose?to=${enc(to)}&subject=${enc(subject)}&body=${enc(body)}`;
  if (provider === "yahoo")
    return `https://compose.mail.yahoo.com/?to=${enc(to)}&subject=${enc(subject)}&body=${enc(body)}`;
  return `mailto:${enc(to)}?subject=${enc(subject)}&body=${enc(body)}`;
}

export default function CustomerSnapshot({ funds, clientId, clientName, onClose, inline, onLogActivity }) {
  const { snapshot, updateGlobalComment, updateFundComment } = useSnapshotStore(clientId);
  const { markSent } = useReportSendLog();
  const { logo } = useLogoStore();
  const { settings } = useSettingsStore();

  // ── Email send panel state ──
  const [emailPanel, setEmailPanel] = useState(false);
  const [hideRecs, setHideRecs] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [sentMsg, setSentMsg] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [sendError, setSendError] = useState("");
  const [pdfAsk, setPdfAsk] = useState(false);

  // Are there any per-fund advisor recommendations? If not, the column is
  // empty — drop it from the report automatically instead of asking.
  const hasRecommendations = Object.values(snapshot.fundComments || {}).some(v => (v || "").trim());

  // Pre-fill client email from overrides when panel opens
  const openEmailPanel = () => {
    const merged = applyClientOverrides(funds[0]?.client || null, clientId);
    setEmailTo(merged?.email || "");
    setHideRecs(!hasRecommendations);
    setEmailPanel(true);
    setPdfAsk(false);
    setSentMsg(false);
  };

  // Fund processing + report HTML live in utils/buildSnapshotReport.js so the
  // bulk monthly send can generate the exact same report for any client.
  const processedFunds = useMemo(() => processSnapshotFunds(funds), [funds]);

  const totals = useMemo(() => computeSnapshotTotals(processedFunds), [processedFunds]);

  const buildReportHtml = (hideRecommendations) =>
    buildSnapshotReportHtml({
      funds, clientName, logo, snapshot, hideRecommendations,
      processedFunds, totals,
    });

  // Generate the report PDF on the server and open it in a new tab.
  // Used by both the header "הפק דוח מסכם" button and the email-panel preview.
  const handleOpenPdf = async (hideRecommendations) => {
    setPreviewing(true);
    setSendError("");
    try {
      const resp = await fetch("/api/preview-pdf", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ html: buildReportHtml(hideRecommendations) }),
      });
      if (!resp.ok) {
        const json = await resp.json().catch(() => ({}));
        const msg = json.error || "שגיאה ביצירת PDF";
        setSendError(msg);
        if (!emailPanel) alert(`⚠️ ${msg}`);
        return;
      }
      const blob = await resp.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch {
      setSendError("שגיאת רשת — נסה שוב");
      if (!emailPanel) alert("⚠️ שגיאת רשת — נסה שוב");
    } finally {
      setPreviewing(false);
    }
  };

  const handleSendEmail = async () => {
    setSending(true);
    setSendError("");
    const fullHtml = buildReportHtml(hideRecs);
    const subject = `דוח מצב פנסיוני — ${clientName}`;
    const agentName = settings.licenseName || "";
    const agentEmail = settings.licenseEmail || settings.contactEmail || "";
    try {
      const { data: { session } } = await supabase?.auth.getSession() ?? { data: {} };
      const jwt = session?.access_token;
      if (!jwt) {
        setSendError("נדרשת התחברות כדי לשלוח מייל. התחבר מחדש ונסה שוב.");
        setSending(false);
        return;
      }
      const resp = await fetch("/api/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` },
        body: JSON.stringify({ to: emailTo, subject, html: fullHtml, agentName, agentEmail, clientName }),
      });
      const json = await resp.json();
      if (resp.status === 503 && json.error === "not_configured") {
        // Resend not configured — download the PDF + open compose window;
        // the agent drags the downloaded file into the email.
        const pdfResp = await fetch("/api/preview-pdf", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ html: fullHtml }),
        });
        if (pdfResp.ok) {
          const blob = await pdfResp.blob();
          const a = document.createElement("a");
          a.href = URL.createObjectURL(blob);
          a.download = `דוח מצב פנסיוני - ${clientName}.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } else {
          await openReport(fullHtml); // PDF failed too — last resort: print window
        }
        const fallbackBody = `שלום,\n\nמצורף דוח מצב פנסיוני עבור ${clientName}.\nה-PDF ירד לתיקיית ההורדות — גרור אותו למייל.\n\nבברכה`;
        window.open(buildComposeUrl(settings.emailProvider || "gmail", emailTo, subject, fallbackBody), "_blank");
      } else if (!resp.ok) {
        setSendError(json.error || "שגיאה בשליחה");
        setSending(false);
        return;
      } else {
        // Real send through Resend succeeded — record it in the send history
        // (the not_configured fallback above only opens a compose window, so
        // it is deliberately NOT recorded).
        markSent(clientId, emailTo, { clientName, kind: "single", subject, period: new Date().toISOString().slice(0, 7) });
      }
      if (onLogActivity) {
        onLogActivity({
          type: "report_sent",
          description: `דוח מסכם נשלח ללקוח${emailTo ? ` (${emailTo})` : ""}`,
          clientName,
          date: new Date().toISOString().slice(0, 10),
        });
      }
      setEmailPanel(false);
      setSentMsg(true);
      setTimeout(() => setSentMsg(false), 3000);
    } catch {
      setSendError("שגיאת רשת — נסה שוב");
    } finally {
      setSending(false);
    }
  };

  const headerExtra = (
    <>
      {sentMsg && (
        <span style={{ fontSize: 12, background: "rgba(255,255,255,0.2)", padding: "4px 10px", borderRadius: 6 }}>
          ✅ המייל נשלח ללקוח
        </span>
      )}
      <button
        onClick={() => hasRecommendations ? setPdfAsk(v => !v) : handleOpenPdf(true)}
        disabled={previewing}
        style={{
          background: "white", border: "none", color: "#1e40af",
          padding: "8px 18px", borderRadius: 8, cursor: previewing ? "default" : "pointer", fontSize: 13, fontWeight: 700,
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)", opacity: previewing ? 0.7 : 1,
        }}>{previewing ? "⏳ מייצר PDF..." : "📄 הפק דוח מסכם"}</button>
      <button onClick={emailPanel ? () => setEmailPanel(false) : openEmailPanel} style={{
        background: emailPanel ? "white" : "rgba(255,255,255,0.15)",
        border: "1px solid rgba(255,255,255,0.5)",
        color: emailPanel ? "#1e40af" : "white",
        padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 700,
      }}>📧 שלח למייל</button>
    </>
  );

  return (
    <Modal title="תמונת לקוח והמלצות" icon="🖼️" color="#1e40af" onClose={onClose} inline={inline} headerExtra={headerExtra}>
      
      {/* ── PDF: include recommendations? ── */}
      {pdfAsk && (
        <div style={{
          background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 12,
          padding: 16, marginBottom: 16, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center",
        }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#dc2626" }}>
            לכלול את המלצות היועץ בדוח?
          </span>
          <button
            onClick={() => { setPdfAsk(false); handleOpenPdf(false); }}
            style={{
              padding: "9px 20px", background: "#1e40af", color: "white",
              border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 13,
            }}
          >✓ עם המלצות</button>
          <button
            onClick={() => { setPdfAsk(false); handleOpenPdf(true); }}
            style={{
              padding: "9px 20px", background: "white", color: "#1e40af",
              border: "1px solid #93c5fd", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 13,
            }}
          >ללא המלצות</button>
          <button onClick={() => setPdfAsk(false)} style={{
            padding: "9px 14px", background: "white", color: "#64748b",
            border: "1px solid #cbd5e1", borderRadius: 8, cursor: "pointer", fontSize: 13,
          }}>✕</button>
        </div>
      )}

      {/* ── Email send panel ── */}
      {emailPanel && (
        <div style={{
          background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 12,
          padding: 16, marginBottom: 16, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end",
        }}>
          <div style={{ flex: "1 1 220px" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#1e3a8a", marginBottom: 4 }}>מייל הלקוח</div>
            <input
              type="email"
              value={emailTo}
              onChange={e => setEmailTo(e.target.value)}
              placeholder="client@example.com"
              style={{
                width: "100%", padding: "8px 10px", border: "1px solid #93c5fd",
                borderRadius: 7, fontSize: 13, outline: "none",
                direction: "ltr", textAlign: "left", boxSizing: "border-box",
              }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={handleSendEmail}
                disabled={!emailTo.trim() || sending}
                style={{
                  padding: "9px 20px", background: emailTo.trim() && !sending ? "#1e40af" : "#93c5fd",
                  color: "white", border: "none", borderRadius: 8, cursor: emailTo.trim() && !sending ? "pointer" : "default",
                  fontWeight: 700, fontSize: 13, whiteSpace: "nowrap",
                }}
              >{sending ? "שולח..." : "📤 שלח דוח במייל"}</button>
              <button
                onClick={() => handleOpenPdf(hideRecs)}
                disabled={previewing}
                style={{
                  padding: "9px 14px", background: "white", color: "#1e40af",
                  border: "1px solid #93c5fd", borderRadius: 8,
                  cursor: previewing ? "default" : "pointer", fontSize: 13, fontWeight: 700, whiteSpace: "nowrap",
                }}
              >{previewing ? "מייצר..." : "👁️ תצוגה מקדימה"}</button>
              <button onClick={() => { setEmailPanel(false); setSendError(""); }} style={{
                padding: "9px 14px", background: "white", color: "#64748b",
                border: "1px solid #cbd5e1", borderRadius: 8, cursor: "pointer", fontSize: 13,
              }}>✕</button>
            </div>
            {hasRecommendations && (
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>
                <input
                  type="checkbox"
                  checked={!hideRecs}
                  onChange={e => setHideRecs(!e.target.checked)}
                />
                כלול המלצות יועץ
              </label>
            )}
            {sendError && (
              <div style={{ fontSize: 12, color: "#dc2626", fontWeight: 600 }}>⚠️ {sendError}</div>
            )}
          </div>
          <div style={{ width: "100%", fontSize: 11, color: "#3b82f6" }}>
            💡 המייל יישלח אל הלקוח; תשובות יגיעו אליך
          </div>
        </div>
      )}

      {/* Executive Summary */}
      <div style={{ background: "white", borderRadius: 12, padding: 20, marginBottom: 24, border: "1px solid #e2e8f0" }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: "#1e3a8a", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
          <span>📝</span> תמצית מנהלים והמלצות כלליות
        </div>
        <textarea
          value={snapshot.globalComment}
          onChange={(e) => updateGlobalComment(e.target.value)}
          placeholder="הזן כאן סיכום כללי של מצב הלקוח והמלצות אסטרטגיות..."
          style={{
            width: "100%", minHeight: 100, padding: 12, borderRadius: 8, border: "1px solid #cbd5e1",
            fontSize: 14, fontFamily: "inherit", outline: "none", resize: "vertical", background: "#f8fafc"
          }}
        />
      </div>

      {/* Funds Table */}
      <div style={{ background: "white", borderRadius: 12, overflow: "hidden", border: "1px solid #e2e8f0" }}>
        <div style={{ padding: "16px 20px", background: "#f1f5f9", borderBottom: "1px solid #e2e8f0", fontWeight: 700, color: "#334155" }}>
          ריכוז מוצרים והמלצות פרטניות
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f8fafc", textAlign: "right" }}>
                <th style={thStyle}>חברה ומוצר</th>
                <th style={thStyle}>יתרה וסיווג</th>
                <th style={thStyle}>מסלולי השקעה</th>
                <th style={thStyle}>הפקדה אחרונה</th>
                <th style={thStyle}>קצבה/מוטבים</th>
              </tr>
            </thead>
            <tbody>
              {processedFunds.map((f, i) => ([
                <tr key={i} style={{ background: f.isFrozen ? "#fff7ed" : "transparent" }}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 700, color: "#1e293b" }}>{f.company}</div>
                    <div style={{ fontSize: 11, color: "#64748b" }}>{f.product} ({f.tochnit})</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#475569", marginTop: 2, letterSpacing: 0.5 }}>{f.polNum}</div>
                    <div style={{
                      display: "inline-block", marginTop: 4, fontSize: 10, fontWeight: 700,
                      padding: "1px 6px", borderRadius: 10,
                      background: f.statusCode === "1" ? "#dcfce7" : "#fee2e2",
                      color:      f.statusCode === "1" ? "#166534" : "#991b1b",
                    }}>{f.statusLabel}</div>
                    {f.isFrozen && <div style={{ fontSize: 10, color: "#ea580c", fontWeight: 700, marginTop: 4 }}>⚠️ פוליסה קפואה</div>}
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 800, color: "#1e40af", fontSize: 15 }}>{fmtMoney(f.redeem)}</div>
                    
                    <div style={{ fontSize: 10, color: "#475569", marginTop: 8, padding: 8, background: "#f8fafc", borderRadius: 6, border: "1px solid #e2e8f0" }}>
                      <div style={{ fontWeight: 700, borderBottom: "1px solid #e2e8f0", marginBottom: 4, paddingBottom: 2 }}>פירוט רכיבים:</div>
                      {f.hk.hon > 0 && <div style={{ display: "flex", justifyContent: "space-between" }}><span>תגמולים (הון):</span> <strong>{fmtMoney(f.hk.hon)}</strong></div>}
                      {f.hk.kitzba > 0 && <div style={{ display: "flex", justifyContent: "space-between" }}><span>תגמולים (קצבה):</span> <strong>{fmtMoney(f.hk.kitzba)}</strong></div>}
                      {f.hk.until2000 > 0 && <div style={{ display: "flex", justifyContent: "space-between" }}><span>תגמולים (עד 2000):</span> <strong>{fmtMoney(f.hk.until2000)}</strong></div>}
                      {f.hk.honP > 0 && <div style={{ display: "flex", justifyContent: "space-between", color: "#b91c1c" }}><span>פיצויים (הון):</span> <strong>{fmtMoney(f.hk.honP)}</strong></div>}
                      {f.hk.kitzbaP > 0 && <div style={{ display: "flex", justifyContent: "space-between", color: "#b91c1c" }}><span>פיצויים (קצבה):</span> <strong>{fmtMoney(f.hk.kitzbaP)}</strong></div>}
                    </div>
                    {(f.dmeiTzv > 0 || f.dmeiHaf > 0) && (
                      <div style={{ fontSize: 10, color: "#475569", marginTop: 4 }}>
                        דמ"צ: <strong>{f.dmeiTzv}%</strong>
                        {f.dmeiHaf > 0 && <> | דמ"ה: <strong>{f.dmeiHaf}%</strong></>}
                      </div>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {(f.groupedMaslulim || []).map((m, i) => (
                      <div key={i} style={{ fontSize: 11, display: "flex", justifyContent: "space-between", gap: 8 }}>
                        <span>{m.name}</span>
                        <strong>{fmtMoney(m.balance)}</strong>
                      </div>
                    ))}
                  </td>
                  <td style={{ ...tdStyle, borderBottom: "none" }}>
                    <div style={{ fontWeight: 600 }}>{fmtMoney(f.lastDepAmt)}</div>
                    <div style={{ fontSize: 11, color: "#64748b" }}>{f.lastDepDate}</div>
                    {f.lastDepEmployer !== "—" && (
                      <div style={{ fontSize: 10, color: "#94a3b8", maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={f.lastDepEmployer}>
                        {f.lastDepEmployer}
                      </div>
                    )}
                  </td>
                  <td style={{ ...tdStyle, borderBottom: "none" }}>
                    <div style={{ color: "#16a34a", fontWeight: 700 }}>
                      {f.pension > 0 ? (
                        <>
                          {fmtMoney(f.pension)}/חודש
                          {f.isMeshalemet && <div style={{ fontSize: 10, color: "#15803d", fontWeight: 400 }}>קופה משלמת</div>}
                        </>
                      ) : "—"}
                    </div>
                    {f.mekadem > 0 && (
                      <div style={{ fontSize: 10, color: "#64748b", fontWeight: 400, marginTop: 2 }}>
                        מקדם קצבה: {f.mekadem.toLocaleString("he-IL", { maximumFractionDigits: 2 })}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 4, cursor: "help" }} title={f.mutavim.map(m => `${m.displayName} (${m.pct}%)`).join(", ")}>
                      👥 {f.mutavim.length} מוטבים
                    </div>
                  </td>
                </tr>,
                <tr key={`${i}-rec`} style={{ borderBottom: "1px solid #e2e8f0", background: f.isFrozen ? "#fff7ed" : "transparent" }}>
                  <td colSpan={5} style={{ padding: "0 16px 12px" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", marginBottom: 4 }}>💬 המלצת יועץ</div>
                    <textarea
                      value={snapshot.fundComments[f.id] || ""}
                      onChange={(e) => updateFundComment(f.id, e.target.value)}
                      placeholder="המלצה לקופה זו..."
                      style={{
                        width: "100%", minHeight: 44, padding: 6, borderRadius: 6, border: "1px solid #e2e8f0",
                        fontSize: 12, fontFamily: "inherit", outline: "none", resize: "vertical", background: "#fafafa", boxSizing: "border-box"
                      }}
                    />
                  </td>
                </tr>
              ])).flat()}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
}

const thStyle = { padding: "12px 16px", fontWeight: 700, color: "#475569", borderBottom: "2px solid #e2e8f0", fontSize: 12 };
const tdStyle = { padding: "12px 16px", verticalAlign: "top" };
